
import { EmfWidgetActionPanelConfigModel, EmfWidgetConfig, EmfTargetConfigDbModel, EmfWidgetDbModel, EmfWidgetType, IOrderComparable, UserWidgetPreferenceModel, EmfTargetMappingModel } from './widget.models';
import { EmfWidgetThemeType } from "./widget.models";
import { CSSProperties } from 'react';
import { useEffect } from "react";
import axios, { AxiosInstance } from 'axios';

class WidgetService {

    private static instance: WidgetService;
    static userIdParameterName = 'userid';
    static usernameParameterName = 'username';
    uniqueScriptClass = 'adgo-emf-widgets-scripts-styles';
    chartLicenseKey = process.env.REACT_APP_AMCHART_LICENSE;
    apiBaseUrl = process.env.REACT_APP_WIDGET_API_URL;
    widgetHostUrl = process.env.REACT_APP_WIDGET_UI_URL;
    apiKey = process.env.REACT_APP_DEMO_ACCESS_KEY;
    functionKey = process.env.REACT_APP_WIDGET_API_KEY;
    gridLicenseKey = process.env.REACT_APP_AGGRID_LICENSE;


    useJsonAttr = false;

    private constructor() { }

    public static getInstance(): WidgetService {
        if (!WidgetService.instance) {
            WidgetService.instance = new WidgetService();
        }
        return WidgetService.instance;
    }

    getAxiosInstance(config: EmfWidgetConfig): AxiosInstance {
        const userId = config.attrKeyValue && config.attrKeyValue[WidgetService.userIdParameterName];
        const username = config.attrKeyValue && config.attrKeyValue[WidgetService.usernameParameterName];
        const instance = axios.create({
            baseURL: this.apiBaseUrl,
            headers: {
                Accept: '*/*',
                'Content-Type': 'application/json',
                'X-Functions-Key': this.functionKey,
                'Access-Key': config.accessKey ?? this.getScriptParamApiKey(),
                'Widget-Type': config.widgetType,
                'User-Id': userId,
                'User-Name': username,
                'Target': config.target
            }
        });
        return instance;
    }

    verifyWidgetKey = async (config: EmfWidgetConfig = null) => {
        return this.getAxiosInstance(config).post<any>('/widgets/verify-key', {})
    }

    getWidgets = async (config: EmfWidgetConfig = null) => {
        return this.getAxiosInstance(config).post<EmfWidgetDbModel[]>('/widgets', {})
    }

    getWidgetDetails = async (config: EmfWidgetConfig = null) => {
        return this.getAxiosInstance(config).get<EmfWidgetDbModel>('/get_widget_details?type=' + config.widgetType, {})
    }

    getTargetConfigDetails = async (config: EmfWidgetConfig, domain: string) => {
        return this.getAxiosInstance(config).get<EmfTargetConfigDbModel>('/get_target_config_details?domain=' + domain)
    }

    getTargetMappingList = async (config: EmfWidgetConfig) => {
        return this.getAxiosInstance(config).get<EmfTargetMappingModel[]>('/get_target_mapping_list?domain=')
    }

    saveTargetConfigDetails = async (config: EmfWidgetConfig, domain: string, widget: EmfWidgetDbModel) => {
        return this.getAxiosInstance(config).post<boolean>('/save_target_config_details?domain=' + domain, widget)
    }

    getRiskDataByReference = async (config: EmfWidgetConfig, riskReference: string) => {
        let url = `/kg_risk_data?riskreference=${riskReference}`;
        try {
            let res = await this.getAxiosInstance(config).get(url);
            return res.data;
        } catch (error) {
            return null
        } 
    }


    getScriptParamApiKey(): string | null {
        let apiKey = null;
        const scriptElements = document.getElementsByTagName('script');
        for (let i = 0; i < scriptElements.length; i++) {
            const currentScript = scriptElements[i];
            const urlToMatch = (this.widgetHostUrl + '/index.js').replace(/([^:]\/)\/+/g, '$1');
            const cleanedCurrentScriptUrl = currentScript.src.replace(/([^:]\/)\/+/g, '$1');
            if (cleanedCurrentScriptUrl.includes(urlToMatch)) {
                const url = new URL(currentScript.src);
                const params = new URLSearchParams(url.search.toLowerCase());
                apiKey = params.get('apikey');
                break;
            }
        }
        return apiKey;
    }

    getBooleanAttrValue(attributeVal: string): boolean {
        if (!attributeVal) return false;
        attributeVal = attributeVal.toLowerCase();
        if (attributeVal == 'false' || attributeVal == '0' || attributeVal == 'n') return false;
        if (attributeVal == 'true' || attributeVal == '1' || attributeVal == 'y') return true;
        return true;
    }

    getWidgetConfig(div: Element): EmfWidgetConfig {

        if (!div) throw Error('invalid element div, undefined');

        let elementHeight = div.clientHeight;
        let parentElement = div;

        do {
            elementHeight = parentElement.clientHeight;
            if (parentElement.parentElement) {
                parentElement = parentElement.parentElement;
            } else {
                break;
            }
        } while (elementHeight <= 0)

        const config = new EmfWidgetConfig();
        config.accessKey = this.getScriptParamApiKey();
        config.height = elementHeight;
        config.attr = div.getAttribute('data-attr');
        config.type = div.getAttribute('data-type');
        config.div = div;

        this.prepareAttributesKeyValue(config);

        return config;
    }

    parseAttributes(attrs: string): { [key: string]: string } {
        let returnValue: { [key: string]: any } = {};
        if (attrs) {
            if (this.useJsonAttr) {
                let fixedJSON = this.getFormatFixedJsonString(attrs);
                let obj = JSON.parse(fixedJSON) as Object;
                for (var key in obj) {
                    if (obj.hasOwnProperty(key)) {
                        returnValue[key.toLowerCase()] = obj[key];
                    }
                }
            } else {
                let keyValuePairs = attrs.split('&');
                for (const pair of keyValuePairs) {
                    const [key, val] = pair.split(/=(.*)/); // Split by the first '=' only
                    if (key && val) {
                        let v = decodeURIComponent(val);
                        let keyPart = key.trim().toLowerCase();
                        let valuePart = v.trim();
                        if (keyPart != 'title' && keyPart != 'subtitle') {
                            valuePart = valuePart.toLowerCase();
                        }
                        if (keyPart != 'undefined' && keyPart != 'null' && valuePart != 'undefined' && valuePart != 'null') {
                            returnValue[keyPart] = valuePart;
                        }
                    }
                }
            }
        }
        return returnValue;
    }

    getBarLoaderCssOveride(): CSSProperties {
        const cssOverrides: CSSProperties = {
            width: "100%",
            height: "2px",
            margin: "auto",
            position: 'absolute',
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            zIndex: 2
        }
        return cssOverrides;
    }

    prepareAttributesKeyValue(config: EmfWidgetConfig) {

        if (!config.actionPanel) {
            config.actionPanel = new EmfWidgetActionPanelConfigModel();
        }

        if (!config.attrKeyValue) {
            config.attrKeyValue = this.parseAttributes(config.attr);
        }

        if (config.attrKeyValue['primary-color']) {
            config.primaryColor = config.attrKeyValue['primary-color'];
        } else if (config.attrKeyValue['primarycolor']) {
            config.primaryColor = config.attrKeyValue['primarycolor'];
        } else if (config.div && config.div.getAttribute('data-primary-color')) {
            config.primaryColor = config.div.getAttribute('data-primary-color') as EmfWidgetThemeType; //TODO
        }

        if (config.attrKeyValue['theme']) {
            config.theme = config.attrKeyValue['theme'] as EmfWidgetThemeType;
        } else if (config.div && config.div.getAttribute('data-theme')) {
            config.theme = config.div?.getAttribute('data-theme') as EmfWidgetThemeType; //TODO
        }

        if (config.attrKeyValue['title']) {
            config.title = config.attrKeyValue['title'];
        }
        if (config.attrKeyValue['subtitle']) {
            config.subTitle = config.attrKeyValue['subtitle'];
        }
        if (config.attrKeyValue['target']) {
            config.target = config.attrKeyValue['target'];
        }

        config.widgetType = this.getWidgetType(config);

        this.prepareLegacyWidgetType(config);
    }

    getWidgetType(config: EmfWidgetConfig): EmfWidgetType {
        let attrKeyValue = config.attrKeyValue;
        let name = attrKeyValue['name'] as EmfWidgetType;
        if (!name) { name = attrKeyValue['type'] as EmfWidgetType; }
        if (!name) { name = config.type as EmfWidgetType }
        return name;
    }

    registerPropsChangeCallback(div: Element, onAttrChange) {

        if (!div) return;

        useEffect(() => {
            const observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    if (mutation.type === 'attributes') {
                        let attrName = mutation.attributeName?.toLowerCase();
                        if (attrName == 'data-attr' || attrName == 'data-theme'
                            || attrName == 'data-primarycolor' || attrName == 'data-primary-color') {
                            let dataSet = mutation.target['dataset'];
                            let attr = dataSet.attr;

                            //legecy compatibility
                            let theme = dataSet.theme;
                            let primaryColor = dataSet.primaryColor || dataSet.themeColor;
                            if (theme && !attr.includes('theme=')) {
                                attr += `&theme=${theme}`
                            }
                            if (primaryColor && !attr.includes('primaryColor=')) {
                                attr += `&primaryColor=${primaryColor}`
                            }
                            //end legecy compatibility

                            onAttrChange(attr);
                        }
                    }
                });
            });

            if (div) {
                observer.observe(div, { attributes: true });
            }

            return () => observer.disconnect(); // Cleanup on unmount
        }, []);
    }

    async saveUserWidgetPreferences(config: EmfWidgetConfig, values: UserWidgetPreferenceModel): Promise<any> {
        const userId = config.attrKeyValue[WidgetService.userIdParameterName];
        if (!userId || userId === 'undefined') {
            return
        }
        const url = `/save_user_widget_preferences?userid=${userId}`;
        const res = await this.getAxiosInstance(config).post(url, values);
        return res.data;
    }

    async getUserWidgetPreferences(config: EmfWidgetConfig): Promise<any> {
        if (!config.attrKeyValue) { return }
        const userId = config.attrKeyValue[WidgetService.userIdParameterName];
        const widgetId = config.widgetType;
        if (!userId || userId === 'undefined') {
            return
        }

        const url = `/get_user_widget_preferences?userid=${userId}&widgetId=${widgetId}`;
        const res = await this.getAxiosInstance(config).get(url);
        return res.data;
    }

    private getFormatFixedJsonString(json: string) {
        if (!json) return json;

        var fixedJSON = json

            // Replace ":" with "@colon@" if it's between double-quotes
            .replace(/:\s*"([^"]*)"/g, function (match, p1) {
                return ': "' + p1.replace(/:/g, '@colon@') + '"';
            })

            // Replace ":" with "@colon@" if it's between single-quotes
            .replace(/:\s*'([^']*)'/g, function (match, p1) {
                return ': "' + p1.replace(/:/g, '@colon@') + '"';
            })

            // Add double-quotes around any tokens before the remaining ":"
            .replace(/(['"])?([a-z0-9A-Z_]+)(['"])?\s*:/g, '"$2": ')

            // Turn "@colon@" back into ":"
            .replace(/@colon@/g, ':');

        return fixedJSON;
    }

    compareOrder = (a: IOrderComparable, b: IOrderComparable) => {
        if (a.order < b.order) {
            return -1;
        }
        if (a.order > b.order) {
            return 1;
        }
        return 0;
    }

    prepareAttrAsQueryString(attrKeyValue: { [key: string]: string }): string {
        var str = [];
        Object.keys(attrKeyValue).forEach(k => {
            str.push(k + "=" + encodeURIComponent(attrKeyValue[k]));
        })
        return str.join("&");
    }

    getClientConfiguredEmfDivs = () => {
        const widgetDivs = document.querySelectorAll("[data-widget='emf']");
        let divs: EmfWidgetConfig[] = [];
        widgetDivs.forEach(div => {
            const config = this.getWidgetConfig(div);
            if (config) {
                divs.push(config);
            }
        });
        return divs;
    }

    private prepareLegacyWidgetType(config: EmfWidgetConfig) {

        //Temp arrangement for old global disaster configs
        if (config.type == 'html' && config.attrKeyValue['url']) {
            config.widgetType = EmfWidgetType.global_disaster_map
        }

        if (config.type == 'chart' && config.attrKeyValue['submission-score']) {
            config.widgetType = EmfWidgetType.submission_score
        }

        if (config.widgetType == EmfWidgetType.new_business_vs_renewal_premium_legacy) { config.widgetType = EmfWidgetType.new_business_vs_renewal_premium }
        if (config.widgetType == EmfWidgetType.conversion_rates_legacy) { config.widgetType = EmfWidgetType.conversion_rates }
        if (config.widgetType == EmfWidgetType.risk_appetite_rates_legacy) { config.widgetType = EmfWidgetType.risk_appetite_rates }
        if (config.widgetType == EmfWidgetType.risk_appetite_ratio_legacy) { config.widgetType = EmfWidgetType.risk_appetite_ratio }
        if (config.widgetType == EmfWidgetType.quoted_vs_bond_premium_legacy) { config.widgetType = EmfWidgetType.quoted_premium_vs_bond_premium }
        if (config.widgetType == EmfWidgetType.business_flow_legacy) { config.widgetType = EmfWidgetType.business_flow }
        if (config.widgetType == EmfWidgetType.risk_status_and_estimated_premium_legacy) { config.widgetType = EmfWidgetType.risk_status_and_esitmated_premium }
        if (config.widgetType == EmfWidgetType.risk_status_and_estimated_premium_legacy2) { config.widgetType = EmfWidgetType.risk_status_and_esitmated_premium }
        if (config.widgetType == EmfWidgetType.exposure_capacity_warnings_legacy) { config.widgetType = EmfWidgetType.exposure_capacity_warnings }
        if (config.widgetType == EmfWidgetType.premium_vs_target_legacy) { config.widgetType = EmfWidgetType.premium_vs_target }
        if (config.widgetType == EmfWidgetType.cybercube_risk_score_legacy) { config.widgetType = EmfWidgetType.cybercube_risk_score }
        if (config.widgetType == EmfWidgetType.premium_paid_info_legacy) { config.widgetType = EmfWidgetType.premium_paid_info }
        if (config.widgetType == EmfWidgetType.recently_booked_legacy) { config.widgetType = EmfWidgetType.recently_booked_risk }
        if (config.widgetType == EmfWidgetType.renewal_history_legacy) { config.widgetType = EmfWidgetType.renewal_history }
        if (config.widgetType == EmfWidgetType.cybercube_risk_signals_legacy) { config.widgetType = EmfWidgetType.cybercube_risk_signals }
        if (config.widgetType == EmfWidgetType.new_and_renewals_by_priority_legacy) { config.widgetType = EmfWidgetType.new_and_renewals_by_priority }
        if (config.widgetType == EmfWidgetType.new_and_renewals_by_priority_legacy2) { config.widgetType = EmfWidgetType.new_and_renewals_by_priority }
        if (config.widgetType == EmfWidgetType.new_and_renewals_by_key_account_legacy) { config.widgetType = EmfWidgetType.new_and_renewals_by_key_account }
        if (config.widgetType == EmfWidgetType.global_disaster_map_legacy) { config.widgetType = EmfWidgetType.global_disaster_map }
        if (config.widgetType == EmfWidgetType.risk_additional_info_legacy) { config.widgetType = EmfWidgetType.risk_additional_info }
        if (config.widgetType == EmfWidgetType.risk_premium_summary_legacy) { config.widgetType = EmfWidgetType.risk_premium_summary }
        if (config.widgetType == EmfWidgetType.industry_classification_legacy) { config.widgetType = EmfWidgetType.industry_classification }
        if (config.widgetType == EmfWidgetType.qliksense_iframe_legacy) { config.widgetType = EmfWidgetType.qliksense_iframe }
        if (config.widgetType == EmfWidgetType.submission_score_legacy) { config.widgetType = EmfWidgetType.submission_score }
        if (config.widgetType == EmfWidgetType.urgent_task_legacy) { config.widgetType = EmfWidgetType.urgent_task }
    
        
    }
}

export { WidgetService }